import {Component, OnDestroy, OnInit} from '@angular/core';
import {MovimentiService} from '../../../_services/movimenti.service';
import {ModalController} from '@ionic/angular';
import {SaveMessage} from '../../../_helpers/save-message';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {Movimento} from '../../../_models/Movimento';
import {TipoContesto} from '../../../_helpers/TipoContesto';
import {AuthenticationService} from '../../../_services/authentication.service';
import {constants} from '../../../_helpers/constants';
import {Contenitore} from '../../../_models/Contenitore';
import {LicenzeService} from '../../../_services/licenze.service';
import {MessageConfirm} from '../../../_helpers/message-confirm.service';
import {TIPO_BOX} from '../../../_helpers/TipoBox';
import {ArticoloEsolver} from '../../../_models/ArticoloEsolver';

export interface DestinazioneContenitore {
  codBox: string;
  codArt: string;
  varianteArt: string;
  tipoBox: number;
  codMag: string;
  codArea: string;
  qta: number;
  qtaUdM2: number;
  isNew: boolean;
  useNuovoArticolo?: boolean;
  hasGiacenza?: boolean;
  // M7 (feature 052): false solo se l'operatore digita un articolo destinazione non
  // selezionato dal catalogo. undefined/true = valido (default = articolo origine, valido).
  articoloValido?: boolean;
}

export interface MovimentoRiepilogo {
  tipo: 'rettifica' | 'scarico' | 'carico';
  codArt: string;
  varianteArt?: string;
  codBox?: string;
  qta: number;
  udM: string;
  lotto?: string;
}

@Component({
  selector: 'app-movimenti-rettifica',
  templateUrl: './movimenti-rettifica.component.html',
  styleUrls: ['./movimenti-rettifica.component.scss'],
})
export class MovimentiRettificaComponent implements OnInit, OnDestroy {
  // Inputs from caller — original values (never mutated after ngOnInit)
  codArt = '';
  varianteArt = '';
  udM1 = '';
  udM2 = '';
  qtaUdM1 = 0;
  qtaUdM2 = 0;
  codLottoAlfa = '';
  codBox = '';
  codMagIn = '';
  codAreaIn = '';
  generaRettifica = true;

  // Saved originals (set once in ngOnInit, never changed)
  giacenzaOriginale = 0;
  giacenzaUdM2Originale = 0;
  codArtOriginale = '';
  varianteArtOriginale = '';
  codLottoAlfaOriginale = '';

  // Editable new values (bound to UI steps)
  nuovoCodArt = '';
  nuovaVarianteArt = '';
  nuovaQtaUdM1 = 0;
  nuovaQtaUdM2 = 0;
  nuovoCodLottoAlfa = '';

  // US4 — spostamento/suddivisione
  qtaNuovaOriginale = 0;
  destinazioni: DestinazioneContenitore[] = [];
  // M2 (feature 052): true quando l'operatore ha modificato manualmente "Nuova quantità
  // contenitore originale", sospendendo il ricalcolo automatico del suggerimento.
  qtaNuovaOriginaleManuale = false;

  // State flags
  loading = false;
  box: Contenitore;
  nuovoArticoloValido = true;
  lottoNonTrovato = false;

  // US1 (feature 052): memoizzazione del riepilogo dietro un fingerprint degli input.
  private _riepilogoCache: MovimentoRiepilogo[] = [];
  private _riepilogoFingerprint = '__init__';

  readonly tipoBoxOptions = TIPO_BOX.filter(t => t.value > 0);
  protected readonly constants = constants;

  constructor(
    private authService: AuthenticationService,
    public licenseService: LicenzeService,
    private organizzazioniService: OrganizzazioniService,
    private movimentiService: MovimentiService,
    private modalCtrl: ModalController,
    private saveMessage: SaveMessage,
    private messageConfirm: MessageConfirm
  ) {}

  ngOnInit() {
    // Save originals before any UI interaction
    this.giacenzaOriginale = this.qtaUdM1;
    this.giacenzaUdM2Originale = this.qtaUdM2;
    this.codArtOriginale = this.codArt;
    this.varianteArtOriginale = this.varianteArt;
    this.codLottoAlfaOriginale = this.codLottoAlfa;

    // Init editable fields from originals
    this.nuovoCodArt = this.codArt;
    this.nuovaVarianteArt = this.varianteArt;
    this.nuovaQtaUdM1 = this.qtaUdM1;
    this.nuovaQtaUdM2 = this.qtaUdM2;
    this.nuovoCodLottoAlfa = this.codLottoAlfa;
    this.qtaNuovaOriginale = this.qtaUdM1;

    // FR-003: mentre la modale è aperta, sopprimi il toast SignalR invasivo sui movimenti.
    this.movimentiService.rettificaModalAperta = true;

    if (this.codBox) {
      this.getContenitore();
    }
  }

  ngOnDestroy() {
    // Ripristina il comportamento di notifica globale alla chiusura della modale.
    this.movimentiService.rettificaModalAperta = false;
  }

  getContenitore = () => {
    this.loading = true;
    this.movimentiService
      .getContenitoreAsync(this.organizzazioniService.organizzazioneId, this.codBox)
      .then((box: Contenitore) => { this.box = box; })
      .catch(() => this.saveMessage.messageError('Errore durante il caricamento del contenitore.'))
      .finally(() => (this.loading = false));
  };

  // --- Computed ---

  get artChanged(): boolean {
    return this.nuovoCodArt !== this.codArtOriginale || this.nuovaVarianteArt !== this.varianteArtOriginale;
  }

  get isArticoloModificato(): boolean {
    return this.artChanged;
  }

  get hasUdm2(): boolean {
    return !!this.udM2;
  }

  get lottoChanged(): boolean {
    return this.nuovoCodLottoAlfa !== this.codLottoAlfaOriginale;
  }

  get deltaUdM1(): number {
    return this.nuovaQtaUdM1 - this.giacenzaOriginale;
  }

  get deltaOriginal(): number {
    return this.giacenzaOriginale - this.qtaNuovaOriginale;
  }

  get qtaResiduaInformativa(): number {
    return this.giacenzaOriginale - this.qtaNuovaOriginale - this.destinazioni.reduce((s, d) => s + (d.qta || 0), 0);
  }

  get canConfirm(): boolean {
    if (this.loading) return false;
    if (this.nuovaQtaUdM1 < 0 || this.nuovaQtaUdM2 < 0) return false;
    if (this.destinazioni.some(d => !d.codBox)) return false;
    if (this.artChanged && !this.nuovoArticoloValido) return false;
    // M6 (FR-014): una destinazione con quantità <= 0 non è ammessa.
    if (this.destinazioni.some(d => (d.qta || 0) <= 0)) return false;
    // M7 (FR-010): articolo destinazione digitato e non trovato in catalogo.
    if (this.destinazioni.some(d => d.articoloValido === false)) return false;
    // M8 (FR-016): il box origine non può essere anche destinazione di sé stesso.
    if (this.destinazioni.some(d => d.codBox && d.codBox === this.codBox)) return false;
    return true;
  }

  // US1 (FR-002): riepilogo memoizzato — ricalcolato solo al cambio del fingerprint,
  // restituendo sempre la stessa istanza di array per stabilizzare l'*ngFor (trackBy).
  get riepilogoMovimenti(): MovimentoRiepilogo[] {
    const fp = this._fingerprint();
    if (fp !== this._riepilogoFingerprint) {
      this._riepilogoFingerprint = fp;
      this._riepilogoCache = this._calcolaRiepilogoMovimenti();
    }
    return this._riepilogoCache;
  }

  get haMovimentiDaEseguire(): boolean {
    return this.riepilogoMovimenti.length > 0;
  }

  trackByRiepilogo(index: number, mov: MovimentoRiepilogo): string {
    return `${mov.tipo}|${mov.codArt}|${mov.codBox}|${mov.lotto}|${index}`;
  }

  private _fingerprint(): string {
    const dest = this.destinazioni
      .map(d => `${d.codBox}|${d.codArt}|${d.varianteArt}|${d.useNuovoArticolo}|${d.qta}|${d.qtaUdM2}`)
      .join(';');
    return [
      this.nuovaQtaUdM1, this.nuovaQtaUdM2, this.nuovoCodArt, this.nuovaVarianteArt,
      this.nuovoCodLottoAlfa, this.qtaNuovaOriginale, dest,
    ].join('#');
  }

  // --- Article / Lot handlers ---

  // C7/US3: testo digitato nel campo articolo. Valido solo se coincide con l'originale;
  // la validità "vera" si ottiene solo selezionando dalla lista (onArticoloSelect).
  onCodArtTyped(value: string) {
    this.nuovoCodArt = value;
    this.nuovoArticoloValido = value === this.codArtOriginale;
  }

  onArticoloSelect(articolo: ArticoloEsolver) {
    if (articolo) {
      this.nuovoCodArt = articolo.codArt;
    }
    this.nuovoArticoloValido = !!articolo;
  }

  onLottoGiacenzaChange(giacenza: number) {
    this.lottoNonTrovato = this.lottoChanged && (!giacenza || giacenza <= 0);
  }

  // --- US4 destination handlers ---

  aggiungiDestinazione() {
    this.destinazioni.push({
      codBox: '',
      codArt: this.codArtOriginale,
      varianteArt: this.varianteArtOriginale,
      tipoBox: 0,
      codMag: this.codMagIn,
      codArea: this.codAreaIn,
      qta: 0,
      qtaUdM2: 0,
      isNew: false,
      useNuovoArticolo: true,
      hasGiacenza: false,
      articoloValido: true,
    });
    this._ricalcolaQtaOriginaleSuggerita();
  }

  rimuoviDestinazione(index: number) {
    this.destinazioni.splice(index, 1);
    // M2: la rimozione di una destinazione re-incrementa il residuo suggerito sull'origine.
    this._ricalcolaQtaOriginaleSuggerita();
  }

  onDestinazioneBoxChange(index: number, codBox: string) {
    const dest = this.destinazioni[index];
    if (!dest) return;
    // M8 (FR-016): il box origine non può essere usato come destinazione.
    if (codBox && codBox === this.codBox) {
      this.saveMessage.messageError(`Il contenitore di origine ${codBox} non può essere anche destinazione.`);
      dest.codBox = '';
      return;
    }
    if (codBox && this.destinazioni.some((d, i) => i !== index && d.codBox === codBox)) {
      this.saveMessage.messageError(`Il contenitore ${codBox} è già presente in un'altra riga di destinazione.`);
      dest.codBox = '';
      return;
    }
    dest.codBox = codBox;
  }

  onDestinazioneGiacenza(index: number, contenitore: Contenitore) {
    const dest = this.destinazioni[index];
    if (!dest) return;
    dest.tipoBox = contenitore.tipoBox ?? 0;
    dest.codMag = contenitore.codMagazzino ?? this.codMagIn;
    dest.codArea = contenitore.codMagArea ?? this.codAreaIn;
    dest.hasGiacenza = (contenitore.giacenza ?? 0) > 0;
    if (dest.hasGiacenza) {
      this.saveMessage.messageWarning(
        `Il contenitore ${contenitore.codice} ha già una giacenza di ${contenitore.giacenza} ${this.udM1}. Il carico si aggiungerà alla giacenza esistente.`
      );
    }
  }

  // M2: variazione quantità di una destinazione → ricalcolo del residuo suggerito.
  onDestinazioneQtaChange() {
    this._ricalcolaQtaOriginaleSuggerita();
  }

  // M7 (FR-010): articolo destinazione digitato (no cambio articolo globale).
  // Campo vuoto = valido (INV-10): il movimento fa fallback all'articolo di origine.
  onDestArticoloTyped(index: number, value: string) {
    const dest = this.destinazioni[index];
    if (!dest) return;
    dest.codArt = value;
    dest.articoloValido = !value || value === this.codArtOriginale;
  }

  onDestArticoloSelect(index: number, articolo: ArticoloEsolver) {
    const dest = this.destinazioni[index];
    if (!dest) return;
    if (articolo) {
      dest.codArt = articolo.codArt;
    }
    dest.articoloValido = !!articolo;
  }

  // M2 (FR-012): l'operatore fissa manualmente la nuova quantità origine (override).
  onQtaNuovaOriginaleManual(value: number) {
    this.qtaNuovaOriginale = value;
    this.qtaNuovaOriginaleManuale = true;
  }

  // M2: finché il valore è "suggerito", residuo = giacenza − Σ destinazioni (mai negativo).
  private _ricalcolaQtaOriginaleSuggerita() {
    if (this.qtaNuovaOriginaleManuale) return;
    const suggerita = this.giacenzaOriginale - this.destinazioni.reduce((s, d) => s + (d.qta || 0), 0);
    this.qtaNuovaOriginale = suggerita > 0 ? suggerita : 0;
  }

  // --- Confirm ---

  confirm = async () => {
    if (this.nuovaQtaUdM1 < 0 || this.nuovaQtaUdM2 < 0) {
      this.saveMessage.messageError('La quantità non può essere negativa.');
      return;
    }

    if (this.destinazioni.some(d => !d.codBox)) {
      this.saveMessage.messageError('Specificare il contenitore per tutte le destinazioni.');
      return;
    }

    // M6 (FR-014): nessuna destinazione a quantità 0.
    if (this.destinazioni.some(d => (d.qta || 0) <= 0)) {
      this.saveMessage.messageError('La quantità di ogni destinazione deve essere maggiore di zero.');
      return;
    }

    // M8 (FR-016): il box origine non può essere anche destinazione.
    if (this.destinazioni.some(d => d.codBox === this.codBox)) {
      this.saveMessage.messageError('Il contenitore di origine non può essere anche destinazione.');
      return;
    }

    if (this.artChanged && !this.nuovoArticoloValido) {
      this.saveMessage.messageError('Articolo non trovato nel catalogo.');
      return;
    }

    // M7 (FR-010): articolo destinazione digitato e non validato.
    if (this.destinazioni.some(d => d.articoloValido === false)) {
      this.saveMessage.messageError('Articolo destinazione non trovato nel catalogo.');
      return;
    }

    // Confirm rettifica a zero (US1, no art/lotto change, no destinations)
    if (!this.artChanged && !this.lottoChanged && this.destinazioni.length === 0
        && this.nuovaQtaUdM1 === 0 && this.giacenzaOriginale > 0) {
      const confirmed = await this.messageConfirm.presentAlert(
        'La quantità sarà azzerata. Confermare l\'operazione?', () => {});
      if (!confirmed) return;
    }

    if (this.lottoChanged && this.lottoNonTrovato) {
      const confirmed = await this.messageConfirm.presentAlert(
        'Il lotto specificato non è nel catalogo. Procedere comunque?', () => {});
      if (!confirmed) return;
    }

    this.loading = true;
    try {
      const { scarichi, carichi } = this._buildMovimenti();

      if (scarichi.length === 0 && carichi.length === 0) {
        await this.modalCtrl.dismiss(true, 'confirm');
        return;
      }

      const ts = Math.floor(Date.now() / 1000);
      scarichi.forEach(m => (m.dataUnixRegistrazione = ts));
      carichi.forEach(m => (m.dataUnixRegistrazione = ts + (scarichi.length > 0 ? 1 : 0)));

      const all = [...scarichi, ...carichi];
      let saved = 0;
      const errors: unknown[] = [];

      for (const m of all) {
        try {
          await this.movimentiService.createMovimentoAsync(m);
          saved++;
        } catch (e) {
          errors.push(e);
        }
      }

      if (errors.length === 0) {
        await this.modalCtrl.dismiss(true, 'confirm');
        return;
      }

      // FR-005: errore parziale → messaggio chiaro + chiusura controllata (refresh chiamante).
      this.saveMessage.messageError(saved > 0
        ? `${saved} di ${all.length} movimenti salvati. Contattare il supporto per i ${errors.length} movimenti non salvati.`
        : 'Errore durante il salvataggio dei movimenti.');
      await this.modalCtrl.dismiss(true, 'confirm');
    } finally {
      this.loading = false;
    }
  };

  cancel = () => this.modalCtrl.dismiss(false, 'cancel');

  // --- Builder methods ---

  // Sorgente unica dei movimenti generati: usata sia da confirm() sia dal riepilogo
  // a video, così il riepilogo coincide sempre con i movimenti reali (FR-017).
  private _buildMovimenti(): { scarichi: Movimento[]; carichi: Movimento[] } {
    if (this.destinazioni.length > 0) {
      return this._buildConDestinazioni();
    }
    if (this.artChanged) {
      return this._buildCambioArticolo();
    }
    if (this.lottoChanged) {
      return this._buildCambioLotto();
    }
    return this._buildDeltaSemplice();
  }

  // Path A/B: presenza di destinazioni (split/spostamento).
  private _buildConDestinazioni(): { scarichi: Movimento[]; carichi: Movimento[] } {
    const scarichi: Movimento[] = [];
    const carichi: Movimento[] = [];
    const cambioArtOLotto = this.artChanged || this.lottoChanged;

    // M1/M3 (INV-4): se articolo o lotto cambiano, lo scarico origine è sempre totale.
    if (cambioArtOLotto) {
      scarichi.push(this._makeScarico(
        this.codArtOriginale, this.varianteArtOriginale, this.codLottoAlfaOriginale,
        this.giacenzaOriginale, this.giacenzaUdM2Originale, this.codBox
      ));
    } else {
      this._aggiungiDeltaOrigine(scarichi, carichi);
    }

    for (const dest of this.destinazioni) {
      const art = this._articoloDestinazione(dest);
      carichi.push(this._makeCaricoDestinazione(art.codArt, art.varianteArt, this.nuovoCodLottoAlfa, dest));
    }

    // M1/M3: rientro del residuo sull'origine con articolo/lotto correnti e UdM2 coerente.
    if (cambioArtOLotto && this.qtaNuovaOriginale > 0) {
      carichi.push(this._makeMovimento(
        this.nuovoCodArt, this.nuovaVarianteArt, this.nuovoCodLottoAlfa,
        this.qtaNuovaOriginale, this._deriveUdM2(this.qtaNuovaOriginale), this.codBox, 1,
        this.codMagIn, this.codAreaIn, this.box?.tipoBox
      ));
    }

    return { scarichi, carichi };
  }

  // Path B senza cambio articolo/lotto: scarico (o carico) del delta sull'origine.
  private _aggiungiDeltaOrigine(scarichi: Movimento[], carichi: Movimento[]) {
    const deltaOrigine = this.giacenzaOriginale - this.qtaNuovaOriginale;
    if (deltaOrigine > 0) {
      // M4 (FR-013): lo scarico origine trasporta la UdM2 reale, non 0 fisso.
      scarichi.push(this._makeScarico(
        this.codArtOriginale, this.varianteArtOriginale, this.codLottoAlfaOriginale,
        deltaOrigine, this._deriveUdM2(deltaOrigine), this.codBox
      ));
    } else if (deltaOrigine < 0) {
      // Raro: giacenza sottostimata — carico di reintegro sull'origine.
      carichi.push(this._makeMovimento(
        this.codArtOriginale, this.varianteArtOriginale, this.nuovoCodLottoAlfa,
        Math.abs(deltaOrigine), this._deriveUdM2(Math.abs(deltaOrigine)), this.codBox, 1,
        this.codMagIn, this.codAreaIn, this.box?.tipoBox
      ));
    }
  }

  // Path C: cambio articolo senza destinazioni (scarico vecchio + carico nuovo).
  private _buildCambioArticolo(): { scarichi: Movimento[]; carichi: Movimento[] } {
    return {
      scarichi: [this._makeScarico(
        this.codArtOriginale, this.varianteArtOriginale, this.codLottoAlfaOriginale,
        this.giacenzaOriginale, this.giacenzaUdM2Originale, this.codBox
      )],
      carichi: [this._makeMovimento(
        this.nuovoCodArt, this.nuovaVarianteArt, this.nuovoCodLottoAlfa,
        this.nuovaQtaUdM1, this.nuovaQtaUdM2, this.codBox, 1,
        this.codMagIn, this.codAreaIn, this.box?.tipoBox
      )],
    };
  }

  // Path D: cambio lotto senza destinazioni (scarico lotto vecchio + carico lotto nuovo).
  private _buildCambioLotto(): { scarichi: Movimento[]; carichi: Movimento[] } {
    return {
      scarichi: [this._makeScarico(
        this.codArtOriginale, this.varianteArtOriginale, this.codLottoAlfaOriginale,
        this.giacenzaOriginale, this.giacenzaUdM2Originale, this.codBox
      )],
      carichi: [this._makeMovimento(
        this.codArtOriginale, this.varianteArtOriginale, this.nuovoCodLottoAlfa,
        this.nuovaQtaUdM1, this.nuovaQtaUdM2, this.codBox, 1,
        this.codMagIn, this.codAreaIn, this.box?.tipoBox
      )],
    };
  }

  // Path E: rettifica delta semplice (stesso articolo/lotto, nessuna destinazione).
  private _buildDeltaSemplice(): { scarichi: Movimento[]; carichi: Movimento[] } {
    const carichi: Movimento[] = [];
    const delta = this.nuovaQtaUdM1 - this.giacenzaOriginale;
    const deltaUdM2 = this.nuovaQtaUdM2 - this.giacenzaUdM2Originale;
    if (delta !== 0 || deltaUdM2 !== 0) {
      carichi.push(this._makeMovimento(
        this.codArtOriginale, this.varianteArtOriginale, this.nuovoCodLottoAlfa,
        delta, deltaUdM2, this.codBox, 1,
        this.codMagIn, this.codAreaIn, this.box?.tipoBox
      ));
    }
    return { scarichi: [], carichi };
  }

  // Articolo da usare per una destinazione: toggle Vecchio/Nuovo se l'articolo è stato
  // cambiato globalmente; altrimenti l'articolo selezionato sulla riga (M7).
  private _articoloDestinazione(dest: DestinazioneContenitore): { codArt: string; varianteArt: string } {
    if (this.artChanged) {
      return dest.useNuovoArticolo !== false
        ? { codArt: this.nuovoCodArt, varianteArt: this.nuovaVarianteArt }
        : { codArt: this.codArtOriginale, varianteArt: this.varianteArtOriginale };
    }
    return { codArt: dest.codArt || this.codArtOriginale, varianteArt: dest.varianteArt || this.nuovaVarianteArt };
  }

  // M1/M4 (INV-6): UdM2 derivata dalla UdM1 per proporzione sulla giacenza originale.
  // Il coefficiente di conversione articolo (coeffX1) non è tracciato in-component, quindi
  // si usa la proporzione documentata come fallback: giacenzaUdM2 × qtaUdM1 / giacenzaUdM1.
  private _deriveUdM2(qtaUdM1: number): number {
    if (!this.hasUdm2 || this.giacenzaOriginale <= 0) return 0;
    return this.giacenzaUdM2Originale * qtaUdM1 / this.giacenzaOriginale;
  }

  private _makeScarico(
    codArt: string, varianteArt: string, codLotto: string,
    quantita: number, quantitaUdM2: number, codBox: string
  ): Movimento {
    return this._makeMovimento(codArt, varianteArt, codLotto, quantita, quantitaUdM2, codBox, -1,
      this.codMagIn, this.codAreaIn, this.box?.tipoBox);
  }

  private _makeCaricoDestinazione(
    codArt: string, varianteArt: string, codLotto: string,
    dest: DestinazioneContenitore
  ): Movimento {
    return this._makeMovimento(
      codArt, varianteArt, codLotto,
      dest.qta, dest.qtaUdM2 ?? 0,
      dest.codBox, 1,
      dest.codMag, dest.codArea, dest.tipoBox
    );
  }

  private _makeMovimento(
    codArt: string, varianteArt: string, codLotto: string,
    quantita: number, quantitaUdM2: number,
    codBox: string, tipoMovimento: number,
    codMag: string, codArea: string, tipoQuantita?: number
  ): Movimento {
    return {
      organizzazioneId: this.organizzazioniService.organizzazioneId,
      id: 0,
      // C1 (FR-006/007): rettifica solo locale (sync OFF) → valorizza dataRegistrazione
      // così il movimento è subito conteggiato nella giacenza. Con sync ON resta null:
      // la valorizza il flusso ESOLVER come oggi.
      dataRegistrazione: this.generaRettifica ? undefined : new Date(),
      dataUnixRegistrazione: 0,
      rifOdP: this.box?.rifOdP,
      idRigaMateriale: 0,
      codArt,
      varianteArt,
      unitaMisura: this.udM1,
      magUdM: this.udM1,
      quantita,
      quantitaUdM2,
      coeffX1: 1,
      codLottoAlfa: codLotto,
      codMagazzinoIn: codMag,
      codMagAreaIn: codArea,
      codBox,
      idAnagrafica: this.box?.idAnagrafica,
      contesto: this.generaRettifica ? TipoContesto.Rettifica : TipoContesto.Produzione,
      tipoMovimento,
      tipoQuantita,
      udM2: this.udM2,
      saldaRiga: false,
      tara: this.box?.tara,
      executedBy: this.organizzazioniService.codiceOperatore ?? this.authService.currentUserValue.userName,
    };
  }

  // --- Preview (riepilogo pre-conferma) ---
  // Derivato dagli stessi movimenti prodotti da _buildMovimenti → il riepilogo a video
  // coincide per costruzione con i movimenti reali (FR-017).

  _calcolaRiepilogoMovimenti(): MovimentoRiepilogo[] {
    const { scarichi, carichi } = this._buildMovimenti();
    const rettificaSemplice = scarichi.length === 0 && !this.artChanged && !this.lottoChanged
      && this.destinazioni.length === 0;
    const showLotto = this.lottoChanged;

    const list: MovimentoRiepilogo[] = [];
    for (const s of scarichi) {
      list.push({
        tipo: 'scarico', codArt: s.codArt, varianteArt: s.varianteArt,
        codBox: s.codBox, qta: s.quantita, udM: this.udM1,
        lotto: showLotto ? s.codLottoAlfa : undefined,
      });
    }
    for (const c of carichi) {
      list.push({
        tipo: rettificaSemplice ? 'rettifica' : 'carico', codArt: c.codArt, varianteArt: c.varianteArt,
        codBox: c.codBox || '(nuovo)', qta: c.quantita, udM: this.udM1,
        lotto: showLotto ? c.codLottoAlfa : undefined,
      });
    }
    return list;
  }
}
